import { Button, Group, Stack, Text, Textarea, useMantineTheme } from '@mantine/core';
import { useState } from 'react';
import { t } from 'i18next';

export type CodeInputProps = {
  value: string | undefined;
  onChange: (value: unknown) => void;
};

type Feedback = { type: 'success' | 'error'; message: string };

const simplifyJsonError = (message: string) => {
  return message.replace(/\s*in JSON at position \d+(\s*\(line \d+ column \d+\))?/i, '');
};

export const CodeInput = ({ value, onChange }: CodeInputProps) => {
  const theme = useMantineTheme();
  const [draft, setDraft] = useState(value ?? '');
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const handleDraftChange = (next: string) => {
    setDraft(next);
    if (feedback) setFeedback(null);
  };

  const handleInsert = () => {
    if (draft.trim() === '') {
      onChange(undefined);
      setFeedback({ type: 'success', message: t('graphEditor.codeInput.inserted') });
      return;
    }

    try {
      JSON.parse(draft);
      onChange(draft);
      setFeedback({ type: 'success', message: t('graphEditor.codeInput.inserted') });
    } catch (e) {
      setFeedback({
        type: 'error',
        message: t('graphEditor.codeInput.invalidJson', {
          message: simplifyJsonError((e as Error).message),
        }),
      });
    }
  };

  const isUnchanged = draft === (value ?? '');

  return (
    <Stack spacing={4}>
      <Textarea
        autosize
        minRows={2}
        value={draft}
        onChange={(e) => handleDraftChange(e.currentTarget.value)}
        styles={{
          input: {
            fontFamily: theme.fontFamilyMonospace,
            fontSize: theme.fontSizes.xs,
          },
        }}
      />
      <Group spacing={8} align="center">
        <Button size="xs" variant="light" onClick={handleInsert} disabled={isUnchanged}>
          {t('graphEditor.codeInput.insert')}
        </Button>
        {feedback && (
          <Text fz="xs" c={feedback.type === 'error' ? 'red' : 'green'}>
            {feedback.message}
          </Text>
        )}
      </Group>
    </Stack>
  );
};
